import { FC, HTMLProps, useEffect, useRef } from "react";
import { BriocheSettings } from "./BriocheSettings";
import { BriocheStyled } from "./Brioche.styled";
import { useDismissSwipe } from "../../lib/useDismissSwipe/useDismissSwipe";

const { IconPart, ContentPart, ControlsPart } = BriocheStyled;

/**
 * A displaying component for Brioche messages.
 * Check the [BriocheEngine](BriocheEngine) class for further details
 *
 * @param className
 * @param settings
 * @param props
 * @constructor
 */
export const Brioche: FC<
  HTMLProps<HTMLDivElement> & { settings: BriocheSettings }
> = ({ className, settings, ...props }) => {
  const swipeDismiss = useDismissSwipe({
    onDismiss: settings.dismissFn
  });
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mainRef.current) {
      return;
    }
    swipeDismiss(mainRef.current);
  }, [swipeDismiss]);

  const settingsClasses = `brioche ${settings.classPosition} ${settings.classPositionActive} ${settings.classVisible}`;

  const {
    content,
    icon,
    iconPosition,
    controls,
    mainPartClass,
    iconPartClass,
    contentPartClass,
    controlsPartClass
  } = settings;

  return (
    <div
      className={`${mainPartClass} ${settingsClasses} ${className ?? ""}`}
      role="status"
      aria-live="assertive"
      aria-atomic="true"
      {...props}
      ref={mainRef}
    >
      {icon && iconPosition === "left" && (
        <IconPart className={iconPartClass} aria-hidden="true">
          {icon}
        </IconPart>
      )}
      <ContentPart className={contentPartClass}>{content}</ContentPart>
      {controls && (
        <ControlsPart className={controlsPartClass}>{controls}</ControlsPart>
      )}
      {icon && iconPosition === "right" && (
        <IconPart className={iconPartClass} aria-hidden="true">
          {icon}
        </IconPart>
      )}
    </div>
  );
};
